import type { InputHTMLAttributes } from 'react'
import { Field, FIELD_CONTROL, FIELD_INVALID } from './Field'
import { cx } from '@/lib/cx'

interface Props
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type' | 'value' | 'onChange'> {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  error?: string | undefined
  hint?: string | undefined
}

/**
 * Everything a person writes around the digits, and nothing else.
 *
 * `type="tel"` sounds like it validates and does not — the HTML spec makes it
 * free text on purpose, because phone formats vary worldwide, and `inputMode`
 * only picks the on-screen keyboard. On a desktop with a real keyboard both are
 * decoration, so "hello world" went into the register as a phone number.
 *
 * Nothing is reformatted: the number is stored as the doctor typed it, and the
 * server matches on the last ten digits.
 */
const ALLOWED = /[^\d\s+()./-]/g

export function PhoneField({
  label,
  value,
  onChange,
  required = false,
  error,
  hint,
  className,
  ...rest
}: Props) {
  return (
    <Field label={label} required={required} error={error} hint={hint}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          value={value}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(event) => {
            const cleaned = event.target.value.replace(ALLOWED, '')
            // A plus is a country code, not a character: only at the front.
            onChange(cleaned.startsWith('+') ? `+${cleaned.slice(1).replace(/\+/g, '')}` : cleaned.replace(/\+/g, ''))
          }}
          className={cx(FIELD_CONTROL, 'tabular-nums', invalid && FIELD_INVALID, className)}
          {...rest}
        />
      )}
    </Field>
  )
}
